import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Calendar, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useRenew } from "@/hooks/useContracts";
import { uploadAttachment } from "@/api/attachments";
import { RENEW_MODE_DEFS } from "./constants";
import type { ContractRenewPayload } from "@/api/types";

interface Props {
  open: boolean;
  onClose: () => void;
  contractId: string;
  contractNumber: string;
}

export function RenewalDrawer({ open, onClose, contractId, contractNumber }: Props) {
  const navigate = useNavigate();
  const panelRef = useDrawerA11y(open, () => onClose());
  const headingId = useId();
  const dateId = useId();
  const notesId = useId();

  const [mode, setMode] = useState<ContractRenewPayload["mode"]>("extend");
  const [newEndDate, setNewEndDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [createTask, setCreateTask] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const renewMut = useRenew(contractId);

  useEffect(() => {
    if (!open) {
      setMode("extend");
      setNewEndDate("");
      setFile(null);
      setNotes("");
      setCreateTask(true);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const needsFile = mode !== "extend";
  const needsDate = mode === "extend";

  const canSubmit =
    (!needsDate || !!newEndDate) &&
    !uploading &&
    !renewMut.isPending;

  const submit = async () => {
    setError(null);
    try {
      let documentFileId: string | undefined;
      if (needsFile && file) {
        setUploading(true);
        const att = await uploadAttachment(file, "CContractAttachment", "file");
        documentFileId = att.id;
      }
      const res = await renewMut.mutateAsync({
        mode,
        newEndDate: newEndDate || undefined,
        documentFileId,
        createTask,
        notes: notes.trim() || undefined,
      });
      if (res.newContractId) {
        navigate(`/contracts/${res.newContractId}`);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося поновити");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
      aria-hidden
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full sm:w-[460px] overflow-y-auto bg-background shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="text-base font-semibold">
            Поновлення договору
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити форму"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <p className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            Договір {contractNumber}. Оберіть спосіб поновлення.
          </p>

          <div className="space-y-2">
            {RENEW_MODE_DEFS.map((m) => (
              <button
                key={m.k}
                type="button"
                onClick={() => setMode(m.k)}
                aria-pressed={mode === m.k}
                className={cn(
                  "w-full text-left rounded-md border p-3 transition-colors",
                  mode === m.k
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50",
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-full border-2",
                      mode === m.k ? "border-primary bg-primary" : "border-muted-foreground/40",
                    )}
                  >
                    {mode === m.k && <Check className="w-2.5 h-2.5 text-primary-foreground" aria-hidden />}
                  </span>
                  <span className="text-sm font-medium">{m.label}</span>
                </div>
                <p className="mt-1 ml-6 text-xs text-muted-foreground">{m.description}</p>
              </button>
            ))}
          </div>

          {needsDate && (
            <div>
              <label htmlFor={dateId} className="mb-1.5 block text-xs font-medium">
                Нова дата закінчення <span className="text-destructive">*</span>
              </label>
              <div className="flex items-center gap-2 rounded-md border px-2.5 py-1.5">
                <Calendar className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                <Input
                  id={dateId}
                  type="date"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  className="border-0 px-0 py-0 h-7 focus-visible:ring-0"
                />
              </div>
            </div>
          )}

          {needsFile && (
            <div>
              <label className="mb-1.5 block text-xs font-medium">
                Документ {mode === "upload" ? "(підписана версія)" : "(нова версія)"}
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className={cn(
                  "block w-full text-sm",
                  "file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90",
                )}
              />
            </div>
          )}

          <div>
            <label htmlFor={notesId} className="mb-1.5 block text-xs font-medium">
              Нотатки <span className="text-muted-foreground">(опц.)</span>
            </label>
            <textarea
              id={notesId}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={createTask}
              onChange={(e) => setCreateTask(e.target.checked)}
              className="rounded border-input"
            />
            <span>Створити задачу-нагадування</span>
          </label>

          {error && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 border-t bg-background px-5 py-4">
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="w-full h-11 text-sm font-semibold"
          >
            {uploading || renewMut.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Збереження…
              </>
            ) : (
              "Поновити"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
